/**
 * Keyboard + mouse input and Pointer Lock ownership.
 *
 * Mouse look is applied outside the fixed step (it is a direct 1:1 mapping and
 * therefore already frame-rate independent); everything else is sampled by the
 * simulation as held state.
 */

import type { MovementInput } from './PlayerMovement';

export type InputAction =
  | 'restart'
  | 'pause'
  /** F: everything in the level a hand can reach for. */
  | 'action'
  /** E: the bottle, and nothing else. */
  | 'sip'
  /** The three boxes at the muurikka. See `BUTTON.pick1`. */
  | 'pick1'
  | 'pick2'
  | 'pick3'
  | 'menuUp'
  | 'menuDown'
  | 'menuSelect'
  | 'toggleDebug'
  | 'toggleColliders';

export class InputManager {
  readonly movement: MovementInput = {
    forward: false,
    back: false,
    left: false,
    right: false,
    jump: false,
  };

  /** Accumulated mouse delta since the last read, in pixels. */
  private mouseDX = 0;
  private mouseDY = 0;

  private readonly actions: InputAction[] = [];
  private readonly held = new Set<string>();
  private pointerLocked = false;

  private onPointerLockChanged: ((locked: boolean) => void) | null = null;

  constructor(private readonly element: HTMLElement) {}

  attach(): void {
    window.addEventListener('keydown', this.handleKeyDown);
    window.addEventListener('keyup', this.handleKeyUp);
    window.addEventListener('blur', this.handleBlur);
    document.addEventListener('pointerlockchange', this.handlePointerLockChange);
    document.addEventListener('mousemove', this.handleMouseMove);
    this.element.addEventListener('mousedown', this.handleMouseDown);
  }

  detach(): void {
    window.removeEventListener('keydown', this.handleKeyDown);
    window.removeEventListener('keyup', this.handleKeyUp);
    window.removeEventListener('blur', this.handleBlur);
    document.removeEventListener('pointerlockchange', this.handlePointerLockChange);
    document.removeEventListener('mousemove', this.handleMouseMove);
    this.element.removeEventListener('mousedown', this.handleMouseDown);
  }

  setPointerLockListener(listener: (locked: boolean) => void): void {
    this.onPointerLockChanged = listener;
  }

  get isPointerLocked(): boolean {
    return this.pointerLocked;
  }

  requestPointerLock(): void {
    if (this.pointerLocked) return;
    // Chrome throttles re-locking right after Escape; failing is not fatal.
    const result = this.element.requestPointerLock?.() as unknown;
    if (result && typeof (result as Promise<void>).catch === 'function') {
      (result as Promise<void>).catch(() => undefined);
    }
  }

  exitPointerLock(): void {
    if (document.pointerLockElement) document.exitPointerLock();
  }

  /** True while any key that should kick a READY run into RUNNING is held. */
  hasStartInput(): boolean {
    for (const code of this.held) {
      if (code !== 'Escape') return true;
    }
    return false;
  }

  /** Reads and clears the accumulated mouse delta. */
  consumeMouseDelta(): { dx: number; dy: number } {
    const delta = { dx: this.mouseDX, dy: this.mouseDY };
    this.mouseDX = 0;
    this.mouseDY = 0;
    return delta;
  }

  /** Reads and clears queued one-shot actions. */
  consumeActions(): InputAction[] {
    if (this.actions.length === 0) return [];
    return this.actions.splice(0, this.actions.length);
  }

  releaseAll(): void {
    this.held.clear();
    this.movement.forward = false;
    this.movement.back = false;
    this.movement.left = false;
    this.movement.right = false;
    this.movement.jump = false;
    this.mouseDX = 0;
    this.mouseDY = 0;
  }

  private syncMovement(): void {
    this.movement.forward = this.held.has('KeyW') || this.held.has('ArrowUp');
    this.movement.back = this.held.has('KeyS') || this.held.has('ArrowDown');
    this.movement.left = this.held.has('KeyA') || this.held.has('ArrowLeft');
    this.movement.right = this.held.has('KeyD') || this.held.has('ArrowRight');
    this.movement.jump = this.held.has('Space');
  }

  private readonly handleKeyDown = (event: KeyboardEvent): void => {
    if (event.repeat) {
      if (event.code === 'F3' || event.code === 'Space') event.preventDefault();
      return;
    }
    switch (event.code) {
      case 'KeyR':
        this.actions.push('restart');
        break;
      // The one action key: doors, the chair, the klapi, the glass in the
      // pirtti's window. See src/world/Interactable.ts.
      case 'KeyF':
        this.actions.push('action');
        break;
      // The bottle, and nothing but the bottle. Kept off F on purpose, and for
      // the same reason F is not overloaded with it.
      case 'KeyE':
        this.actions.push('sip');
        break;
      // The number row, which is the whole of the aamupala minigame: three
      // boxes, three keys, one second. Deliberately not F - a key that meant
      // "open the door" everywhere else and "grab the falafel" at one grill is
      // a key nobody can use under a clock. The numpad answers too, because
      // somebody will have a hand on it.
      case 'Digit1':
      case 'Numpad1':
        this.actions.push('pick1');
        break;
      case 'Digit2':
      case 'Numpad2':
        this.actions.push('pick2');
        break;
      case 'Digit3':
      case 'Numpad3':
        this.actions.push('pick3');
        break;
      case 'Escape':
        this.actions.push('pause');
        break;
      // The arrows are two things at once: Kimmo's legs while the game owns the
      // mouse, and the front page's selection while it does not. Only the menu
      // knows which is true, so both readings are queued unconditionally and
      // `Game` drops the one it is not in a state to use. The held state at the
      // bottom of this handler is recorded either way, so movement is untouched.
      case 'ArrowUp':
        this.actions.push('menuUp');
        break;
      case 'ArrowDown':
        this.actions.push('menuDown');
        break;
      case 'Enter':
      case 'NumpadEnter':
        this.actions.push('menuSelect');
        break;
      case 'F3':
        event.preventDefault();
        this.actions.push('toggleDebug');
        break;
      case 'F4':
        event.preventDefault();
        this.actions.push('toggleColliders');
        break;
      case 'Space':
        event.preventDefault();
        break;
      default:
        break;
    }
    this.held.add(event.code);
    this.syncMovement();
  };

  private readonly handleKeyUp = (event: KeyboardEvent): void => {
    this.held.delete(event.code);
    this.syncMovement();
  };

  private readonly handleBlur = (): void => {
    this.releaseAll();
  };

  private readonly handleMouseMove = (event: MouseEvent): void => {
    if (!this.pointerLocked) return;
    this.mouseDX += event.movementX;
    this.mouseDY += event.movementY;
  };

  private readonly handleMouseDown = (): void => {
    if (!this.pointerLocked) this.requestPointerLock();
  };

  private readonly handlePointerLockChange = (): void => {
    this.pointerLocked = document.pointerLockElement === this.element;
    // A key used to enter the game must not also wake the player.
    this.releaseAll();
    this.onPointerLockChanged?.(this.pointerLocked);
  };
}
